import dayjs from "dayjs";
import { compareDates } from "@/shared/utils";
import { DATE_FORMAT } from "@/shared/constants";
import { DateFilter, type DateFilterProps } from "@/common/DateFilter";
import { DEFAULT_NOTE_TYPES, NOTE_STATUS } from "@/services/api";
import type { NotesTableColumn, NotesTableColumnGetter, NotesTableColumnsProps } from "../types";
import type { NotesTableRowData } from "../types/notes-table-types";
import { NOTES_TABLE_COLUMNS_KEYS } from "../notes-table-columns-keys";
import { getRowTextColor } from "../../lib/get-row-text-color";
import { DateCell } from "../../ui";

export type NotesTableDueDateColumnProps = NotesTableColumnsProps<{
  onChangeFilterDateFrom?: DateFilterProps["onChangeFrom"];
  onChangeFilterDateTo?: DateFilterProps["onChangeTo"];
  defaultValueFilterFrom?: DateFilterProps["defaultValueFrom"];
  defaultValueFilterTo?: DateFilterProps["defaultValueTo"];
}>;

export type NotesTableDueDateColumnGetter = NotesTableColumnGetter<NotesTableDueDateColumnProps>;

export const getNotesTableDueDateColumn: NotesTableDueDateColumnGetter = ({
  title = "Due date",
  dataIndex = ["dueDate"],
  key = NOTES_TABLE_COLUMNS_KEYS.dueDate,
  width = 175,
  minWidth = 175,
  defaultValueFilterFrom,
  defaultValueFilterTo,
  onChangeFilterDateFrom = () => "",
  onChangeFilterDateTo = () => "",
  filterDropdown,
  showSorterTooltip = false,

  render,
  sorter = (a: NotesTableRowData, b: NotesTableRowData) =>
    !isNaN(compareDates({ firstDate: a?.noteDate, secondDate: b?.noteDate }))
      ? compareDates({ firstDate: a?.noteDate, secondDate: b?.noteDate }) > 0
        ? -1
        : 1
      : isNaN(dayjs(a?.noteDate, DATE_FORMAT).day())
        ? -1
        : 1,
  ...props
}) => {
  const defaultDateFilter = () => (
    <DateFilter
      defaultValueFrom={defaultValueFilterFrom}
      defaultValueTo={defaultValueFilterTo}
      onChangeFrom={onChangeFilterDateFrom}
      onChangeTo={onChangeFilterDateTo}
    />
  );

  const defaultRender: NotesTableColumn["render"] = (_, record) => {
    return (
      <DateCell
        textColor={getRowTextColor(
          record,
          DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT === record.noteType.name,
        )}
        disabled={
          record.status === NOTE_STATUS.DONE &&
          record.noteType.name === DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT
        }
        date={record.dueDate}
      />
    );
  };

  return {
    title: title,
    dataIndex: dataIndex,
    width: width,
    minWidth: minWidth,
    key: key,
    sorter: sorter,
    filterDropdown: filterDropdown ? filterDropdown : defaultDateFilter,
    render: render ? render : defaultRender,
    showSorterTooltip: showSorterTooltip,
    ...props,
  };
};
